import {LowestRate, ShoppingCartItem} from '../schema'
import {booking} from '@whyhotel/common-schema'
import currency from 'currency.js'
import {calcOfferPriceSummary} from '@whyhotel/ibe-pricing'

const MONTHLY_CANCELLATION_CODE = 'LTSTAY'

/**
 * Determines if an offer is monthly
 * @param offer The booking offer
 * @returns True, if it's a monthly offer
 */
export function isMonthlyOffer(offer: booking.BookingOffer): boolean {
  return offer?.cancellationFee?.code === MONTHLY_CANCELLATION_CODE
}

/**
 * Determines if the offer requires payment at the time of booking
 * @param offer The offer
 * @returns
 */
export function isPrepaidOffer(offer: booking.BookingOffer): boolean {
  return (
    offer.pricingSummary?.totalPrepayment &&
    offer.pricingSummary?.totalPrepayment > 0
  )
}

export function calcTotalBaseRate(offer: booking.BookingOffer): number {
  return currency(offer.pricingSummary.totalRoomCharges).add(
    offer.pricingSummary.totalIncludedServices
  ).value
}

export function calcNightlyBaseRate(offer: booking.BookingOffer): number {
  const nights = offer.timeSlices.length
  const totalBaseRate = calcTotalBaseRate(offer)
  return currency(totalBaseRate).divide(nights).value
}

export function getLowestRateForUnitGroup(
  unitGroupOffer: booking.UnitGroupOffer
): LowestRate {
  return unitGroupOffer.offers.reduce(
    (acc: LowestRate, offer: booking.BookingOffer) => {
      const totalBaseRate = calcTotalBaseRate(offer)
      const nightlyBaseRate = calcNightlyBaseRate(offer)

      if (acc.nightly === -1 || nightlyBaseRate < acc.nightly) {
        return {
          total: totalBaseRate,
          nightly: nightlyBaseRate,
          currency: offer.totalGrossAmount.currency
        }
      }
      return acc
    },
    {
      total: -1,
      nightly: -1,
      currency: ''
    }
  )
}

export function getLowestRateForProperty(
  unitGroupOffers: booking.UnitGroupOffer[]
): LowestRate {
  return unitGroupOffers.reduce(
    (acc: LowestRate, unitGroupOffer: booking.UnitGroupOffer) => {
      // get lowest rate for unitgroup
      const lowestUnitGroupRate = getLowestRateForUnitGroup(unitGroupOffer)

      // check if is lowest rate for all unit groups
      if (acc.nightly === -1 || lowestUnitGroupRate.nightly < acc.nightly) {
        return {
          total: lowestUnitGroupRate.total,
          nightly: lowestUnitGroupRate.nightly,
          currency: lowestUnitGroupRate.currency
        }
      } else {
        return acc
      }
    },
    {
      total: -1,
      nightly: -1,
      currency: ''
    }
  )
}

export function calcCartPriceSummary(
  cartItems: ShoppingCartItem[]
): booking.ShoppingCartPriceSummary {
  const priceSummaries = cartItems.map(cartItem => {
    // calculate offer price summary for each cart item including add-on services
    return calcOfferPriceSummary(cartItem.offer, cartItem.additionalServices)
  })
  const totalRoomCharges = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.totalRoomCharges).value,
    0
  )
  const totalIncludedServices = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.totalIncludedServices).value,
    0
  )
  const totalAdditionalServices = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.totalAdditionalServices).value,
    0
  )
  const totalTaxesAndFees = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.totalTaxesAndFees).value,
    0
  )
  const totalPrepayment = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.totalPrepayment).value,
    0
  )
  const total = priceSummaries.reduce(
    (acc, item) => currency(acc).add(item.total).value,
    0
  )
  const balance = currency(total).subtract(totalPrepayment).value

  return {
    totalRoomCharges,
    totalIncludedServices,
    totalAdditionalServices,
    totalTaxesAndFees,
    totalPrepayment,
    total,
    balance
  }
}
